/**
 * 岗位管理列表页面
 */
import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Space, Input, Form, Select,
  Modal, message, Tag, Popconfirm, Statistic, Row, Col
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
  TeamOutlined, UserOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getPositionList, createPosition, updatePosition, deletePosition,
  batchDeletePositions, getPositionStatistics, getPositionCategories
} from '../../api/position';
import type { PositionInfo, PositionQueryParams, PositionFormData, PositionStatistics } from '../../api/types';
import PositionForm from './PositionForm';
import PositionUsers from './PositionUsers';

const { confirm } = Modal;

const PositionList: React.FC = () => {
  const [list, setList] = useState<PositionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [usersModalVisible, setUsersModalVisible] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<PositionInfo | null>(null);
  const [statistics, setStatistics] = useState<PositionStatistics | null>(null);
  const [categories, setCategories] = useState<{ value: string; label: string }[]>([]);
  const [searchForm] = Form.useForm();

  // 获取岗位列表
  const fetchPositionList = async () => {
    setLoading(true);
    try {
      const formValues = searchForm.getFieldsValue();
      const params: PositionQueryParams = {
        ...formValues,
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      const res = await getPositionList(params);
      if (res?.data) {
        setList(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('获取岗位列表失败', error);
    } finally {
      setLoading(false);
    }
  };

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      const res = await getPositionStatistics();
      if (res?.data) {
        setStatistics(res.data);
      }
    } catch (error) {
      console.error('获取统计数据失败', error);
    }
  };

  // 获取岗位类别
  const fetchCategories = async () => {
    try {
      const res = await getPositionCategories();
      if (res?.data?.categories) {
        setCategories(res.data.categories.map(c => ({
          value: c.value,
          label: c.label,
        })));
      }
    } catch (error) {
      console.error('获取岗位类别失败', error);
    }
  };

  // 初始化加载
  useEffect(() => {
    fetchPositionList();
    fetchStatistics();
    fetchCategories();
  }, [pagination.current, pagination.pageSize]);

  // 处理表格变更
  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  // 搜索
  const handleSearch = () => {
    setPagination({ ...pagination, current: 1 });
    fetchPositionList();
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setPagination({ ...pagination, current: 1 });
    fetchPositionList();
  };

  // 新增岗位
  const handleAdd = () => {
    setCurrentPosition(null);
    setFormModalVisible(true);
  };

  // 编辑岗位
  const handleEdit = (record: PositionInfo) => {
    setCurrentPosition(record);
    setFormModalVisible(true);
  };

  // 查看岗位用户
  const handleViewUsers = (record: PositionInfo) => {
    setCurrentPosition(record);
    setUsersModalVisible(true);
  };

  // 删除岗位
  const handleDelete = async (id: number) => {
    try {
      await deletePosition(id);
      message.success('删除成功');
      fetchPositionList();
      fetchStatistics();
    } catch (error) {
      console.error('删除失败', error);
    }
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('请至少选择一条记录');
      return;
    }

    confirm({
      title: '确定要删除所选岗位吗?',
      icon: <ExclamationCircleOutlined />,
      content: '此操作将永久删除所选岗位，是否继续？',
      onOk: async () => {
        try {
          await batchDeletePositions(selectedRowKeys.map(key => Number(key)));
          message.success('删除成功');
          fetchPositionList();
          fetchStatistics();
          setSelectedRowKeys([]);
        } catch (error) {
          console.error('删除失败', error);
        }
      }
    });
  };

  // 表单提交成功的回调
  const handleFormSuccess = () => {
    setFormModalVisible(false);
    fetchPositionList();
    fetchStatistics();
  };

  // 岗位类别标签颜色
  const getCategoryColor = (category: string) => {
    const colorMap: Record<string, string> = {
      management: 'purple',
      technical: 'blue',
      sales: 'green',
      support: 'orange',
      other: 'default',
    };
    return colorMap[category] || 'default';
  };

  // 岗位类别显示文本
  const getCategoryLabel = (category: string) => {
    const found = categories.find(c => c.value === category);
    return found?.label || category;
  };

  const columns: ColumnsType<PositionInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: '岗位名称',
      dataIndex: 'name',
      width: 150,
    },
    {
      title: '岗位编码',
      dataIndex: 'code',
      width: 120,
    },
    {
      title: '岗位类别',
      dataIndex: 'category',
      width: 100,
      render: (category) => (
        <Tag color={getCategoryColor(category)}>
          {getCategoryLabel(category)}
        </Tag>
      ),
    },
    {
      title: '级别',
      dataIndex: 'level',
      width: 80,
      render: (level) => level || '-',
    },
    {
      title: '人数',
      dataIndex: 'userCount',
      width: 80,
      render: (count) => (
        <Space>
          <UserOutlined />
          {count || 0}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 1 ? 'success' : 'default'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort',
      width: 80,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<TeamOutlined />}
            size="small"
            onClick={() => handleViewUsers(record)}
          >
            人员
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除此岗位吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="position-list-container">
      {/* 统计卡片 */}
      {statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="总岗位数"
                value={(statistics as any).totalPositions ?? (statistics as any).total_positions ?? statistics.total ?? 0}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="启用岗位"
                value={(statistics as any).activePositions ?? Object.keys((statistics as any).byCategory || {}).length ?? 0}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="总在岗人数"
                value={(statistics as any).totalUsers ?? 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="岗位类别"
                value={Object.keys((statistics as any).byCategory || {}).length ?? 0}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <Card title="岗位管理" bordered={false}>
        {/* 搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input placeholder="岗位名称/编码" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="category">
            <Select
              placeholder="岗位类别"
              style={{ width: 120 }}
              allowClear
              options={categories}
            />
          </Form.Item>
          <Form.Item name="status">
            <Select
              placeholder="状态"
              style={{ width: 100 }}
              allowClear
            >
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增岗位
            </Button>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleBatchDelete}
              disabled={!selectedRowKeys.length}
            >
              批量删除
            </Button>
          </Space>
        </div>

        {/* 表格 */}
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          loading={loading}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 岗位表单对话框 */}
      <PositionForm
        visible={formModalVisible}
        position={currentPosition}
        categories={categories}
        onCancel={() => setFormModalVisible(false)}
        onSuccess={handleFormSuccess}
      />

      {/* 岗位用户对话框 */}
      <PositionUsers
        visible={usersModalVisible}
        position={currentPosition}
        onCancel={() => setUsersModalVisible(false)}
        onRefresh={() => {
          fetchPositionList();
          fetchStatistics();
        }}
      />
    </div>
  );
};

export default PositionList;
